/**
 * Activación unificada de la pantalla «No pudimos cargar».
 *
 * Incluye un GUARD ANTI-BUCLE de recarga: registramos cada recarga manual en
 * localStorage con marca de tiempo (sobrevive al cierre total del WebView, a
 * diferencia de sessionStorage). Si se acumulan demasiadas recargas en poco
 * tiempo, la UI ofrece «Continuar sin recargar» en vez de recargar otra vez,
 * rompiendo el ciclo carga→recarga→carga del APK.
 */
import { usePageLoadErrorStore } from "@/store/pageLoadErrorStore";

export const APP_LOAD_ERROR_TITLE = "No pudimos cargar";

export const APP_LOAD_ERROR_MESSAGE =
  "Puede ser tu conexión o que el servidor no respondió a tiempo.\npara pedir los datos otra vez";

export const APP_LOAD_ERROR_BUTTON = "Recargar página";

// ── Reporte de error consciente de navegación ───────────────────────────────
// La pantalla fatal NO debe aparecer por requests que quedaron obsoletos al
// navegar rápido. Por eso el reporte es debounced (settle) y ligado a la ruta:
// si cambiaste de ruta (o se resolvió bien) durante el settle, se cancela.

/** Margen para confirmar que la página realmente no carga (no fue navegación). */
const SETTLE_MS = 700;

/** Sube en cada cambio de ruta. Un reporte solo se muestra si el epoch no cambió. */
let navEpoch = 0;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

/** ¿MSW (mock local) aún no intercepta? Durante el warmup api.ts ya reintenta. */
function isMswWarmingUp(): boolean {
  const w = window as Window & {
    __altenotMswEnabled?: boolean;
    __altenotMswReady?: boolean;
  };
  return !!w.__altenotMswEnabled && !w.__altenotMswReady;
}

/** Llamar en cada cambio de ruta: invalida reportes pendientes de la ruta vieja. */
export const bumpNavEpoch = (): void => {
  navEpoch += 1;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
};

/**
 * Programa el overlay de carga fallida tras un settle. Se cancela si navegas
 * o si otra carga tuvo éxito (clearPageLoadFailure) durante ese lapso.
 */
export const reportPageLoadFailure = (): void => {
  // Durante el calentamiento de MSW api.ts reintenta; no mostrar fatal aún.
  if (isMswWarmingUp()) return;

  const epochAtReport = navEpoch;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    // Si navegamos durante el settle, este reporte quedó obsoleto.
    if (epochAtReport !== navEpoch) return;
    if (isMswWarmingUp()) return;
    usePageLoadErrorStore.getState().show();
  }, SETTLE_MS);
};

export const clearPageLoadFailure = (): void => {
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  usePageLoadErrorStore.getState().clear();
};

// ── Guard anti-bucle de recarga ─────────────────────────────────────────────

const RELOAD_LOG_KEY = "altenot_page_reload_log";
const RELOAD_WINDOW_MS = 60_000;
const RELOAD_MAX = 3;

function readReloadLog(): number[] {
  try {
    const raw = localStorage.getItem(RELOAD_LOG_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? (arr as number[]) : [];
  } catch {
    return [];
  }
}

function pruneLog(log: number[]): number[] {
  const cutoff = Date.now() - RELOAD_WINDOW_MS;
  return log.filter((t) => typeof t === "number" && t >= cutoff);
}

/** ¿Se han acumulado demasiadas recargas en la ventana de tiempo reciente? */
export function isReloadLoopDetected(): boolean {
  return pruneLog(readReloadLog()).length >= RELOAD_MAX;
}

/** Registra una recarga manual (llamar justo antes de window.location.reload()). */
export function recordReloadAttempt(): void {
  try {
    const log = pruneLog(readReloadLog());
    log.push(Date.now());
    localStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(log));
  } catch {
    /* ignorar */
  }
}

/** Limpia el contador (tras un arranque sano o al continuar sin recargar). */
export function resetReloadLoopGuard(): void {
  try {
    localStorage.removeItem(RELOAD_LOG_KEY);
  } catch {
    /* ignorar */
  }
}
